import { useEffect } from 'react';
import type { RefObject } from 'react';
import type { SubtitleConversion, SubtitleInfo } from '../../desktop-media';
import { subtitlesUrl } from '../../desktop-media';

/** Remembered subtitle language: an ISO code, or "off". */
const PREFERENCE_KEY = 'viva.subtitleLanguage';
const POLL_MS = 3000;

/** ISO 639-2 codes in files ("spa", "fre") to the two-letter codes browsers use. */
const TWO_LETTER: Record<string, string> = {
  ara: 'ar', baq: 'eu', eus: 'eu', cat: 'ca', chi: 'zh', zho: 'zh', cze: 'cs', ces: 'cs', dan: 'da', dut: 'nl', nld: 'nl', eng: 'en', fin: 'fi',
  fre: 'fr', fra: 'fr', ger: 'de', deu: 'de', glg: 'gl', gre: 'el', ell: 'el', heb: 'he', hin: 'hi', hun: 'hu', ita: 'it', jpn: 'ja', kor: 'ko',
  nor: 'no', pol: 'pl', por: 'pt', rum: 'ro', ron: 'ro', rus: 'ru', spa: 'es', swe: 'sv', tur: 'tr', ukr: 'uk', vie: 'vi', per: 'fa', fas: 'fa',
};

export function languageCode(language: string | null): string {
  if (!language) return '';
  const code = language.toLowerCase();
  return TWO_LETTER[code] ?? code;
}

/** "Español", "English", "Français · Forced": the language in its own words, plus the track's title. */
export function subtitleLabel(track: SubtitleInfo): string {
  const code = languageCode(track.language);
  let name = '';
  try {
    const names = new (Intl as unknown as { DisplayNames: new (locales: string[], options: { type: string }) => { of(code: string): string | undefined } }).DisplayNames([code], { type: 'language' });
    name = code ? (names.of(code) ?? '') : '';
  } catch {
    // Older engines (TV) without Intl.DisplayNames: the code is still informative.
    name = code.toUpperCase();
  }
  name = name ? name.charAt(0).toUpperCase() + name.slice(1) : `Subtitles ${track.index + 1}`;
  return track.title && !name.toLowerCase().includes(track.title.toLowerCase()) ? `${name} · ${track.title}` : name;
}

export function readSubtitlePreference(): string | null {
  try {
    return localStorage.getItem(PREFERENCE_KEY);
  } catch {
    return null;
  }
}

export function saveSubtitlePreference(language: string | null): void {
  try {
    localStorage.setItem(PREFERENCE_KEY, language || 'off');
  } catch {
    // Not remembering only costs a tap next time.
  }
}

/** Cues from a WebVTT file. */
export function parseVtt(text: string): { start: number; end: number; text: string }[] {
  const seconds = (value: string) => value.split(':').reduce((total, part) => total * 60 + Number(part), 0);
  const cues: { start: number; end: number; text: string }[] = [];
  for (const block of text.replace(/\r/g, '').split(/\n\n+/)) {
    const lines = block.split('\n');
    const at = lines.findIndex((line) => line.includes('-->'));
    if (at < 0) continue;
    const times = /([\d:.]+)\s+-->\s+([\d:.]+)/.exec(lines[at]!);
    const body = lines.slice(at + 1).join('\n').trim();
    if (times && body) cues.push({ start: seconds(times[1]!), end: seconds(times[2]!), text: body });
  }
  return cues;
}

/**
 * Subtitles inside a converted file (browser version): one text track per
 * language on the video element, filled as the relay writes them during the
 * conversion. They appear in the player's Subtitles menu; the remembered
 * language is turned on by itself.
 */
export function useConversionSubtitles(videoRef: RefObject<HTMLVideoElement | null>, subtitles: SubtitleConversion | undefined): void {
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !subtitles || subtitles.tracks.length === 0 || typeof VTTCue === 'undefined') return undefined;
    const preference = readSubtitlePreference();
    const tracks = subtitles.tracks.map((info) => {
      const element = document.createElement('track');
      element.kind = 'subtitles';
      element.label = subtitleLabel(info);
      const code = languageCode(info.language);
      if (code) element.srclang = code;
      video.appendChild(element);
      element.track.mode = preference && preference !== 'off' && preference === code ? 'showing' : 'hidden';
      return { info, element, added: 0 };
    });
    // Only the first matching track is turned on.
    let shown = false;
    for (const track of tracks) {
      if (track.element.track.mode !== 'showing') continue;
      if (shown) track.element.track.mode = 'hidden';
      shown = true;
    }

    let active = true;
    const poll = async () => {
      for (const track of tracks) {
        const url = subtitlesUrl(subtitles.session, track.info.index);
        if (!url) continue;
        try {
          const cues = parseVtt(await (await fetch(url)).text());
          if (!active) return;
          for (const cue of cues.slice(track.added)) track.element.track.addCue(new VTTCue(cue.start, cue.end, cue.text));
          track.added = Math.max(track.added, cues.length);
        } catch {
          // The relay may be briefly unreachable: the next round catches up.
        }
      }
    };
    void poll();
    const timer = setInterval(() => void poll(), POLL_MS);
    return () => {
      active = false;
      clearInterval(timer);
      for (const track of tracks) track.element.remove();
    };
  }, [videoRef, subtitles]);
}
